import {
  Cell,
  CellResult,
  ClientMessage,
  DirectoryContentsMessage,
  NotebookContent,
  NotebookDataResultsMessage,
  ServerMessage,
  TinkerBoxFileOrDirectory
} from '@convergence/types';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import { deepCopy } from '../lib/deepCopy';
import { WebSocketClient } from '../lib/WebSocketClient';
import {
  ConnectionState,
  TinkerBoxConnectionStore
} from './TinkerBoxConnectionStore';

// example notebook as json
// {"cells": [{"id": "1", "source": ["# Untitled Notebook\n", "\n", "This is your own personal notebook. Feel free to edit, rename, save, or delete."], "results": [], "metadata": {}, "cell_type": "markdown"}, {"id": "2", "source": ["# Code cell ready to be run...\n", "echo 'Hello!'"], "outputs": [], "results": [], "metadata": {"trusted": true, "collapsed": false}, "cell_type": "code", "execution_count": null}], "metadata": {"kernelspec": {"name": "python2", "language": "python", "display_name": "Python 2"}, "language_info": {"name": "python", "version": "2.7.9", "mimetype": "text/x-python", "file_extension": ".py", "pygments_lexer": "ipython2", "codemirror_mode": {"name": "ipython", "version": 2}, "nbconvert_exporter": "python"}}, "nbformat": 4, "nbformat_minor": 0}

export const MarkdownOrCode = {
  markdown: 'markdown',
  code: 'code'
} as const;
export type MarkdownOrCode =
  (typeof MarkdownOrCode)[keyof typeof MarkdownOrCode];

export class NotebookStore {
  notebookContent: NotebookContent | null;
  activeCellId: string | null;
  socket: WebSocketClient;
  error: string | null = null;
  currentDirectory = '';
  directoryContents: TinkerBoxFileOrDirectory[] = [];
  tinkerBoxConnection: TinkerBoxConnectionStore;

  constructor(
    socket: WebSocketClient,
    tinkerBoxConnection: TinkerBoxConnectionStore
  ) {
    makeAutoObservable(this);
    this.notebookContent = null;
    this.activeCellId = null;
    this.socket = socket;
    this.tinkerBoxConnection = tinkerBoxConnection;

    this.onRunCode = this.onRunCode.bind(this);
    this.moveCell = this.moveCell.bind(this);
    this.updateCell = this.updateCell.bind(this);
    this.deleteCell = this.deleteCell.bind(this);
    this.changeCellType = this.changeCellType.bind(this);
  }

  init() {
    this.socket.onMessage(ServerMessage.error, this.onError);
    this.socket.onMessage(
      ServerMessage.directoryContents,
      this.onDirectoryContents
    );
  }

  setContent(newContent: NotebookContent) {
    this.notebookContent = newContent;
  }

  refreshDirectory() {
    this.getDirectoryContents(this.currentDirectory);
  }

  onError(err: unknown) {
    const e = z.string().parse(err);
    this.error = e;
    toast.error(e);
  }

  getDirectoryContents(path: string) {
    console.log('getting directory contents');
    this.currentDirectory = path;
    this.socket.sendMessage({
      event: ClientMessage.directoryContents,
      data: { path }
    });
  }

  onDirectoryContents(data: unknown) {
    const validated = DirectoryContentsMessage.parse(data);
    this.directoryContents = validated;
  }

  activateCell = (id: string) => {
    this.activeCellId = id;
  };

  async sendCommand(
    command: string
  ): Promise<z.infer<typeof NotebookDataResultsMessage>[]> {
    if (!this.socket) {
      console.log('no socket');
      return [];
    }
    const messageId = uuid();
    const results: z.infer<typeof NotebookDataResultsMessage>[] = [];
    let finished = false;
    const resultsEvent = ServerMessage.notebookDataResults + messageId;
    const finishedEvent = ServerMessage.notebookDataFinished + messageId;

    return new Promise((resolve, reject) => {
      const onResult = (data: unknown) => {
        console.log(`onResult data: ${JSON.stringify(data)}`);
        const validated = NotebookDataResultsMessage.safeParse(data);
        if (validated.success) {
          results.push(validated.data);
        } else {
          console.log('error');
          reject(validated.error);
        }
      };
      const onFinished = () => {
        console.log('finished');
        if (!finished) {
          finished = true;
          unsubscribeResult();
          unsubscribeFinished();
          resolve(results);
        }
      };
      const unsubscribeResult = this.socket.onMessage(resultsEvent, onResult);
      const unsubscribeFinished = this.socket.onMessage(
        finishedEvent,
        onFinished
      );
      this.socket.sendMessage({
        event: ClientMessage.notebookData,
        data: JSON.stringify({ command, id: messageId })
      });
    });
  }

  async onRunCode(cellSourceStr: string, cellId: string) {
    // console.log(this.socket.readyState);
    // console.log(this.tinkerBoxConnection.connectionState);
    if (
      this.socket.readyState === WebSocket.OPEN &&
      this.tinkerBoxConnection.connectionState === ConnectionState.connected &&
      this.notebookContent
    ) {
      const command = cellSourceStr;
      const results = await this.sendCommand(command);
      if (!results || results.length === 0) {
        toast.error('No code result :(');
        return;
      }
      console.log('sendCommand result:');
      console.log({ result: results });

      const cellToUpdate = this.notebookContent.cells.findIndex(
        (c) => c.id === cellId
      );
      if (!this.notebookContent.cells[cellToUpdate]) {
        return;
      }
      let cellResults: CellResult[] = [];
      for (const resObj of results) {
        for (const res of resObj.results) {
          cellResults.push({
            content: res.content,
            is_error: res.isError,
            show: resObj.resultType === 'action'
          });
        }
      }
      this.notebookContent.cells[cellToUpdate].results = cellResults;
      this.notebookContent = deepCopy(this.notebookContent);
    } else if (
      this.socket.readyState === WebSocket.OPEN &&
      this.tinkerBoxConnection.connectionState === ConnectionState.disconnected
    ) {
      toast('Connect to a Tinker Box to run code');
    } else {
      toast.error('Error: no connection');
    }
  }

  addCell(indexToAdd: number) {
    const newCell: Cell = {
      id: uuid(),
      results: [],
      cell_type: 'code',
      execution_count: null,
      metadata: {
        collapsed: false,
        trusted: true
      },
      outputs: [],
      source: ['# Code cell ready to be run...\n']
    };
    this.notebookContent?.cells.splice(indexToAdd, 0, newCell);
    // this.notebook = deepCopy(this.notebook);
  }

  moveCell(startIndex: number, endIndex: number) {
    if (!this.notebookContent || !this.notebookContent.cells[startIndex]) {
      return;
    }
    const cellsCopy = this.notebookContent.cells.toSpliced(startIndex, 1);
    cellsCopy.splice(endIndex, 0, this.notebookContent.cells[startIndex]);
    this.notebookContent.cells = cellsCopy;
    // this.notebook = deepCopy(this.notebook);
  }

  updateCell(contents: string, cellId: string) {
    const lines = contents.split('\n');
    const cellToUpdate = this.notebookContent?.cells.find(
      (c) => c.id === cellId
    );
    if (cellToUpdate) {
      cellToUpdate.source = lines;
      // this.notebook = deepCopy(this.notebook);
    }
  }

  changeCellType(newType: MarkdownOrCode, cellId: string) {
    const cellToUpdate = this.notebookContent?.cells.find(
      (c) => c.id === cellId
    );
    if (cellToUpdate) {
      cellToUpdate.cell_type = newType;
      // this.notebook = deepCopy(this.notebook);
    }
  }

  deleteCell(index: number) {
    this.notebookContent?.cells.splice(index, 1);
    // this.notebook = deepCopy(this.notebook);
  }
}
